import type { Database } from "@punchup/data/supabase.types";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import { QueryClient, useQuery } from "@tanstack/react-query";
import { SentryFeatureTags, logException } from "@punchup/sentry";
import {
  VenuePageCustomLink,
  getVenuePageCustomLinks,
} from "../../data/venue_pages";

import { SupabaseClient } from "@supabase/supabase-js";

export function useVenuePageCustomLinks(venuePageId: string): {
  customLinks: VenuePageCustomLink[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const queryFn = async (): Promise<VenuePageCustomLink[]> => {
    const supabase = createClientComponentClient<Database>();

    try {
      const customLinks = await getVenuePageCustomLinks(supabase, venuePageId);
      return customLinks;
    } catch (error) {
      logException(error as Error, {
        extra: {
          data: `error fetching custom links for venue page: ${venuePageId}`,
        },
        tags: {
          feature: SentryFeatureTags.VenuePages,
          supabase: true,
        },
      });
      throw error;
    }
  };

  const {
    data: customLinks = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["venue-page-custom-links", venuePageId],
    queryFn,
  });

  return { customLinks, isLoading, error, refetch };
}

export async function prefetchVenuePageCustomLinks(
  queryClient: QueryClient,
  supabase: SupabaseClient<Database>,
  venuePageId: string
) {
  await queryClient.prefetchQuery({
    queryKey: ["venue-page-custom-links", venuePageId],
    queryFn: async () => {
      return await getVenuePageCustomLinks(supabase, venuePageId);
    },
    staleTime: 5 * 60 * 1000,
  });
}
